import { useState, useEffect } from "react";
import axios from "axios";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  ArrowLeft, Loader2, Sword, Sparkles, AlertTriangle, Info, Clock,
  Check, X as XIcon, ShieldCheck, Zap,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import { S, SLOT_META, GRADE_COLOR, KatanaDivider, SamuraiBackground } from "@/components/samurai/theme";
import SenseiPanel from "@/components/SenseiPanel";
import { checkSenseiEligibility } from "@/lib/sensei";
import { playSfx } from "@/lib/audio";
import { Plus } from "lucide-react";

const SELECTIONS_KEY = "bc_my_bets";
const getSessionId = () => {
  let sid = localStorage.getItem("betcracker_session_id");
  if (!sid) {
    sid = crypto.randomUUID?.() || Math.random().toString(36).slice(2);
    localStorage.setItem("betcracker_session_id", sid);
  }
  return sid;
};

/**
 * Convert a single Samurai slot pick into a "bet" object compatible with
 * the Mie Pronostici flow (POST /api/user-bets). Returns null when the
 * slot doesn't carry a single, well-defined market (e.g. unavailable).
 */
function slotToBet(slotKey, data) {
  // The API doesn't expose a generic "active" flag — a slot is considered
  // available whenever the payload exists and carries its market fields.
  // Per-slot presence checks below fall back to ``null`` when a slot is
  // genuinely empty (e.g. NO-BET shinobi, missing scores in gundam).
  if (!data) return null;
  if (slotKey === "shinobi") {
    if (!data.bet) return null;
    return {
      type: "single", source: "samurai_shinobi",
      bet: data.bet,
      prob: data.prob,
      odds: data.book_odds || null,
    };
  }
  if (slotKey === "samurai") {
    if (!data.dc?.label || !data.gol?.label) return null;
    return {
      type: "combo", source: "samurai_samurai",
      bet: `${data.dc.label} + ${data.gol.label}`,
      prob: data.combined_prob,
      odds: data.estimated_odds || null,
    };
  }
  if (slotKey === "shogun") {
    if (!data.bet) return null;
    return {
      type: "value", source: "samurai_shogun",
      bet: data.bet,
      prob: data.prob,
      odds: data.book_odds || null,
      value_pct: data.value_pct ?? null,
    };
  }
  if (slotKey === "kage") {
    if (!data.home?.band || !data.away?.band) return null;
    return {
      type: "combo", source: "samurai_kage",
      bet: `Multigol Casa ${data.home.band} + Trasferta ${data.away.band}`,
      prob: data.sum_prob,
      odds: data.fair_odds || null,
    };
  }
  if (slotKey === "gundam") {
    // Save only the top score — the full set would clutter Mie Pronostici.
    const top = (data.scores || [])[0];
    if (!top) return null;
    return {
      type: "scoreexact", source: "samurai_gundam",
      bet: `Risultato esatto ${top.score}`,
      prob: top.prob,
      odds: top.odds || null,
    };
  }
  return null;
}

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatKickoff(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString("it-IT", {
      hour: "2-digit", minute: "2-digit", timeZone: "Europe/Rome",
    });
  } catch {
    return "—";
  }
}

function formatDateFull(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("it-IT", {
      weekday: "short", day: "2-digit", month: "short", timeZone: "Europe/Rome",
    });
  } catch {
    return "";
  }
}

function OutcomeBadge({ outcome }) {
  if (!outcome || outcome === "unknown") return null;
  const won = outcome === "won";
  const color = won ? S.win : S.loss;
  const bg = won ? S.winDim : S.lossDim;
  const Icon = won ? Check : XIcon;
  return (
    <span
      data-testid={`outcome-${outcome}`}
      style={{
        display: "inline-flex", alignItems: "center", gap: "0.3rem",
        padding: "0.2rem 0.55rem",
        background: bg, border: `1px solid ${color}60`,
        color, fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 900,
        fontSize: "0.65rem", letterSpacing: "0.25em", textTransform: "uppercase",
        textShadow: `0 0 8px ${color}90`,
        boxShadow: `0 0 12px ${color}30`,
      }}
    >
      <Icon size={11} strokeWidth={3} />
      {won ? "vinto" : "perso"}
    </span>
  );
}

function ReasonChips({ reasons, color, glow, testid }) {
  if (!reasons || reasons.length === 0) return null;
  return (
    <div
      data-testid={testid || "slot-reasons"}
      style={{
        display: "flex", flexWrap: "wrap", gap: "0.4rem",
        marginTop: "0.75rem",
        paddingTop: "0.75rem",
        borderTop: `1px dashed ${S.hairline2}`,
      }}
    >
      {reasons.map((r, i) => (
        <span
          key={`${r}-${i}`}
          style={{
            display: "inline-flex", alignItems: "center", gap: "0.35rem",
            padding: "0.22rem 0.55rem",
            background: `${color}14`,
            border: `1px solid ${color}55`,
            color: S.ink,
            fontFamily: "'JetBrains Mono',monospace",
            fontSize: "0.6rem", letterSpacing: "0.08em",
            textTransform: "uppercase",
            boxShadow: `0 0 8px ${glow}`,
          }}
        >
          <span style={{
            width: 5, height: 5, background: color,
            boxShadow: `0 0 6px ${color}`, flexShrink: 0,
          }} />
          {r}
        </span>
      ))}
    </div>
  );
}

function AdvancedSignalsPanel({ adv }) {
  if (!adv) return null;
  const elms = adv.elms_factor;
  const incl = adv.inclinazione;
  const trap = adv.tension_trap;
  const dom = (adv.tendenza || {}).dom;
  const items = [];
  if (elms != null) {
    const color = elms >= 60 ? S.win : elms <= 40 ? S.blood : S.steel;
    items.push({
      label: "Elm (Difese)", value: `${elms.toFixed(0)}/100`, color,
      hint: elms >= 60 ? "solide" : elms <= 40 ? "fragili" : "medie",
    });
  }
  if (incl != null) {
    const color = incl >= 60 ? S.blood : incl <= 40 ? S.steel : S.sub;
    items.push({
      label: "Inclinazione", value: `${incl.toFixed(0)}%`, color,
      hint: incl >= 60 ? "verso casa" : incl <= 40 ? "verso ospite" : "equilibrio",
    });
  }
  if (trap != null) {
    const extreme = trap > 125 || trap < 75;
    items.push({
      label: "Tension Trap", value: `${trap}`, color: extreme ? S.shogun : S.sub,
      hint: extreme ? "rumorosa" : "coerente",
    });
  }
  if (dom) {
    items.push({
      label: "Tendenza", value: dom === "home" ? "Casa" : dom === "away" ? "Trasferta" : "Pari",
      color: dom === "home" ? S.blood : dom === "away" ? S.steel : S.sub,
      hint: "dominio elastico",
    });
  }
  if (items.length === 0) return null;

  return (
    <div
      data-testid="samurai-advanced-panel"
      style={{
        marginTop: "0.75rem",
        padding: "0.85rem 1rem",
        background: S.panel,
        border: `1px solid ${S.hairline2}`,
        borderTop: `1px solid ${S.neon}40`,
        boxShadow: `0 0 14px rgba(34,255,163,0.08)`,
      }}
    >
      <div
        style={{
          fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700,
          fontSize: "0.65rem", letterSpacing: "0.3em", color: S.neon,
          textTransform: "uppercase", marginBottom: "0.55rem",
          textShadow: `0 0 8px ${S.neonGlow}`,
        }}
      >
        Segnali avanzati · alimentano gli slot
      </div>
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${items.length}, 1fr)`, gap: "0.6rem" }}>
        {items.map((it) => (
          <div key={it.label} style={{ minWidth: 0 }}>
            <div style={{
              fontFamily: "'JetBrains Mono',monospace",
              fontSize: "0.55rem", letterSpacing: "0.15em",
              textTransform: "uppercase", color: S.muted, marginBottom: 2,
            }}>
              {it.label}
            </div>
            <div style={{
              fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 800,
              fontSize: "1.1rem", color: it.color, lineHeight: 1,
              textShadow: `0 0 8px ${it.color}60`,
            }}>
              {it.value}
            </div>
            <div style={{
              fontFamily: "'DM Sans',sans-serif", fontSize: "0.7rem",
              color: S.sub, fontStyle: "italic", marginTop: 2,
            }}>
              {it.hint}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const SHIELD_CHECK_META = {
  mc_ok: {
    label: "Modelli concordi",
    desc: "Poisson+ELO+Forma confermati da 10k simulazioni Monte Carlo (divergenza < 6%)",
  },
  ci_ok: {
    label: "Intervallo stretto",
    desc: "Anche nel 10° percentile la probabilità resta ≥ 55% (pick stabile al bootstrap)",
  },
  hyper_ok: {
    label: "Hyper Combo",
    desc: "La combo coincide con quella calcolata dalla matrice Dixon-Coles (Hyper Combo)",
  },
  keeper_clear: {
    label: "Portiere neutro",
    desc: "Nessun portiere avversario in stato di grazia che possa condizionare i gol",
  },
};

function ShieldBadge({ checks, color, glow, testid }) {
  const [open, setOpen] = useState(false);
  // Close on outside tap (mobile)
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    const t = setTimeout(() => document.addEventListener("click", close), 0);
    return () => {
      clearTimeout(t);
      document.removeEventListener("click", close);
    };
  }, [open]);

  if (!checks || !checks.total) return null;

  // List the checks applicable to this slot (in display order). Values:
  //   true  → passed       (green check)
  //   false → failed       (red X)
  //   null  → not applicable to this market (grey dash)
  const items = ["mc_ok", "ci_ok", "hyper_ok", "keeper_clear"]
    .filter((k) => k in checks)
    .map((k) => ({
      key: k,
      state: checks[k] === true ? "ok" : checks[k] === false ? "ko" : "na",
      ...SHIELD_CHECK_META[k],
    }));

  // Use the backend's authoritative passed/total. It treats "non applicable"
  // correctly: the denominator is the full slot-level check count (3 for
  // Shinobi/Samurai, 2 for Shogun), so the user sees "1/3" instead of "1/1"
  // and never has the impression that the pick was checked against a single
  // filter only.
  const total = checks.total || items.length;
  const passed = typeof checks.passed === "number"
    ? checks.passed
    : items.filter((it) => it.state === "ok").length;
  const ratio = total > 0 ? passed / total : 0;
  const tint =
    ratio >= 1 ? color :
    ratio >= 0.67 ? "#FFB020" :
    "#FF4040";
  const tintGlow =
    ratio >= 1 ? glow :
    ratio >= 0.67 ? "rgba(255,176,32,0.45)" :
    "rgba(255,64,64,0.45)";

  return (
    <span
      data-testid={testid}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
      style={{
        position: "relative",
        display: "inline-flex", alignItems: "center", gap: "0.3rem",
        padding: "0.2rem 0.45rem",
        fontFamily: "'JetBrains Mono',monospace", fontSize: "0.62rem", fontWeight: 700,
        color: tint, letterSpacing: "0.06em",
        border: `1px solid ${tint}60`,
        background: `${tint}12`,
        boxShadow: `0 0 8px ${tintGlow}`,
        cursor: "help",
        userSelect: "none",
      }}
    >
      <ShieldCheck size={10} style={{ filter: `drop-shadow(0 0 3px ${tint})` }} />
      {passed}/{total}
      {open && (
        <div
          data-testid={`${testid}-popover`}
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "absolute", top: "calc(100% + 8px)", right: 0,
            zIndex: 50, width: 280,
            background: "rgba(8,12,10,0.97)",
            border: `1px solid ${tint}80`,
            boxShadow: `0 0 24px ${tintGlow}, 0 8px 30px rgba(0,0,0,0.6)`,
            padding: "0.75rem 0.9rem",
            fontFamily: "'DM Sans',sans-serif",
            textAlign: "left",
            cursor: "default",
          }}
        >
          <div style={{
            display: "flex", alignItems: "center", gap: "0.4rem",
            borderBottom: `1px solid ${tint}30`, paddingBottom: "0.5rem",
            marginBottom: "0.5rem",
          }}>
            <ShieldCheck size={12} style={{ color: tint, filter: `drop-shadow(0 0 4px ${tint})` }} />
            <span style={{
              fontFamily: "'Barlow Condensed',sans-serif", fontSize: "0.8rem",
              fontWeight: 800, letterSpacing: "0.2em", color: tint,
              textTransform: "uppercase",
            }}>
              Affidabilità {passed}/{total}
            </span>
          </div>
          {items.map((it) => {
            const iconColor =
              it.state === "ok" ? "#22FFA3" :
              it.state === "ko" ? "#FF6B6B" :
              "#6C727A";
            const labelColor =
              it.state === "na" ? "#9CA3AF" : "#E8EAE8";
            const descText =
              it.state === "na"
                ? "Non applicabile a questo mercato"
                : it.desc;
            return (
              <div key={it.key} style={{
                display: "flex", gap: "0.5rem", alignItems: "flex-start",
                marginBottom: "0.45rem",
                opacity: it.state === "na" ? 0.65 : 1,
              }}>
                <span style={{
                  flexShrink: 0, width: 14, height: 14,
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  marginTop: 2,
                  color: iconColor,
                  filter: `drop-shadow(0 0 4px ${iconColor})`,
                }}>
                  {it.state === "ok" && <Check size={12} strokeWidth={3} />}
                  {it.state === "ko" && <XIcon size={12} strokeWidth={3} />}
                  {it.state === "na" && <span style={{
                    fontSize: "14px", fontWeight: 700, lineHeight: 1,
                  }}>—</span>}
                </span>
                <div style={{ minWidth: 0 }}>
                  <div style={{
                    fontSize: "0.72rem", fontWeight: 700, color: labelColor,
                    letterSpacing: "0.02em",
                  }}>
                    {it.label}
                  </div>
                  <div style={{
                    fontSize: "0.68rem", lineHeight: 1.35,
                    color: it.state === "na" ? "#6C727A" : S.sub,
                    marginTop: 1,
                    fontStyle: it.state === "na" ? "italic" : "normal",
                  }}>
                    {descText}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </span>
  );
}

function BushidoCoherenceBanner({ data }) {
  if (!data || !data.active) return null;
  const GREEN = "#22FFA3";
  return (
    <div
      data-testid="bushido-coherence-banner"
      style={{
        position: "relative",
        marginBottom: "1.25rem",
        padding: "1rem 1.25rem",
        background: "linear-gradient(135deg, rgba(10, 22, 16, 0.95) 0%, rgba(6, 40, 24, 0.95) 50%, rgba(10, 22, 16, 0.95) 100%)",
        border: `1px solid ${GREEN}`,
        overflow: "hidden",
        animation:
          "bushidoFlashIn 600ms cubic-bezier(0.2, 0.8, 0.15, 1) both, " +
          "bushidoPulse 2.6s ease-in-out 600ms infinite",
      }}
    >
      {/* moving light shine */}
      <div
        aria-hidden
        style={{
          position: "absolute", inset: 0, pointerEvents: "none",
          background: `linear-gradient(120deg, transparent 25%, ${GREEN}33 48%, ${GREEN}55 50%, ${GREEN}33 52%, transparent 75%)`,
          transform: "translateX(-100%)",
          animation: "bushidoShine 1800ms ease-in-out 400ms 1 both",
        }}
      />
      {/* header */}
      <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", position: "relative" }}>
        <Sparkles size={16} style={{ color: GREEN, filter: `drop-shadow(0 0 8px ${GREEN})` }} />
        <span style={{
          fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 900,
          fontSize: "1.05rem", letterSpacing: "0.25em",
          color: GREEN, textShadow: `0 0 10px ${GREEN}, 0 0 22px ${GREEN}88`,
          textTransform: "uppercase",
        }}>
          {data.title || "Bushido Coherence"}
        </span>
      </div>
      <div style={{
        position: "relative",
        marginTop: "0.45rem",
        fontFamily: "'DM Sans',sans-serif", fontSize: "0.82rem",
        color: "#D5F6E2", lineHeight: 1.4,
      }}>
        {data.description || "Convergenza tra Shinobi, Samurai e Gundam! Match molto interessante!"}
      </div>
      {Array.isArray(data.convergent_scores) && data.convergent_scores.length > 0 && (
        <div style={{
          position: "relative",
          display: "flex", alignItems: "center", gap: "0.5rem",
          marginTop: "0.65rem", flexWrap: "wrap",
        }}>
          <span style={{
            fontFamily: "'JetBrains Mono',monospace", fontSize: "0.62rem",
            color: "#90E9B8", letterSpacing: "0.15em", textTransform: "uppercase",
          }}>
            Gundam convergenti:
          </span>
          {data.convergent_scores.map((s, i) => (
            <span key={i} style={{
              fontFamily: "'JetBrains Mono',monospace", fontWeight: 700,
              fontSize: "0.7rem",
              padding: "0.18rem 0.5rem",
              color: GREEN,
              background: `${GREEN}18`,
              border: `1px solid ${GREEN}66`,
              boxShadow: `0 0 8px ${GREEN}55`,
            }}>
              {s.label}{s.prob ? ` · ${s.prob}%` : ""}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function SlotCard({ slotKey, data, dataTestId, isFinished, onAdd, saved }) {
  const meta = SLOT_META[slotKey];
  const Icon = meta.icon;
  const available = data != null;
  const outcome = available && isFinished ? data.outcome : null;
  const checks = available ? data.checks : null;
  // Show the add-to-mie-pronostici button only for live, available, and
  // upcoming (not yet finished) slots.
  const canAdd = available && !isFinished && !!onAdd;

  return (
    <div
      data-testid={dataTestId}
      style={{
        position: "relative",
        background: S.panel,
        border: `1px solid ${S.hairline2}`,
        borderLeft: `2px solid ${meta.color}`,
        padding: "1rem 1.2rem",
        opacity: available ? 1 : 0.5,
        boxShadow: available
          ? `-8px 0 26px -10px ${meta.glow}, inset 1px 0 0 ${meta.color}30`
          : "none",
      }}
    >
      {/* Header row */}
      <div
        style={{
          display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "0.75rem",
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center",
            background: meta.bg, border: `1px solid ${meta.color}60`,
            boxShadow: `0 0 12px ${meta.glow}`,
          }}
        >
          <Icon size={14} style={{ color: meta.color, filter: `drop-shadow(0 0 4px ${meta.color})` }} />
        </div>
        <span
          style={{
            fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 900,
            fontSize: "0.95rem", letterSpacing: "0.3em", color: meta.color,
            textShadow: `0 0 10px ${meta.glow}`,
          }}
        >
          {meta.label}
        </span>
        <span
          style={{
            fontFamily: "'DM Sans',sans-serif", fontSize: "0.72rem", color: S.muted,
            fontStyle: "italic",
          }}
        >
          · {meta.sub}
        </span>
        <span style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <ShieldBadge checks={checks} color={meta.color} glow={meta.glow} testid={`${dataTestId}-shield`} />
          <OutcomeBadge outcome={outcome} />
          {canAdd && (
            <button
              data-testid={`${dataTestId}-add-bet`}
              onClick={(e) => { e.stopPropagation(); onAdd(); }}
              disabled={saved}
              title={saved ? "Aggiunto a 'I Miei Pronostici'" : "Aggiungi a 'I Miei Pronostici'"}
              style={{
                display: "flex", alignItems: "center", gap: "0.3rem",
                padding: "0.25rem 0.55rem",
                background: saved ? "rgba(34,197,94,0.15)" : "transparent",
                color: saved ? "#22C55E" : meta.color,
                border: `1px solid ${saved ? "rgba(34,197,94,0.5)" : `${meta.color}55`}`,
                fontFamily: "'Barlow Condensed',sans-serif",
                fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.12em",
                textTransform: "uppercase", cursor: saved ? "default" : "pointer",
                transition: "all .2s",
              }}>
              {saved ? <><Check size={11} strokeWidth={3} /> Salvato</> : <><Plus size={11} strokeWidth={3} /> Aggiungi</>}
            </button>
          )}
        </span>
      </div>

      {!available ? (
        <p
          style={{
            fontFamily: "'JetBrains Mono',monospace", fontSize: "0.72rem",
            color: S.muted, margin: 0,
          }}
        >
          {slotKey === "shinobi" && "Nessun bet sicuro con quota ≥ 1.20 e prob ≥ 70%"}
          {slotKey === "samurai" && "Nessuna combo Doppia Chance + Gol supera le soglie (65% + 55%)"}
          {slotKey === "gundam" && "Non sono disponibili proiezioni sui risultati esatti"}
          {slotKey === "shogun" && "Nessun value bet con probabilità > 60% per questa partita"}
          {slotKey === "kage" && "Multigol per squadra non sufficientemente convergente (somma < 130 o banda < 55%)"}
        </p>
      ) : slotKey === "samurai" ? (
        <>
          <div
            style={{
              fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700,
              fontSize: "1.3rem", color: S.ink, lineHeight: 1.15, marginBottom: "0.6rem",
              textShadow: `0 0 10px ${meta.glow}`,
            }}
          >
            {data.dc.label}
            <span style={{ color: meta.color, margin: "0 0.4em" }}>+</span>
            {data.gol.label}
          </div>
          <div style={{ display: "flex", gap: "1.5rem", flexWrap: "wrap" }}>
            <Metric label="Prob combinata" value={`${data.combined_prob}%`} accent={meta.color} bold />
            {data.estimated_odds != null && (
              <Metric
                label={data.odds_source === "fair" ? "Quota fair" : "Quota stimata"}
                value={data.estimated_odds.toFixed(2)}
                accent={data.odds_source === "fair" ? S.sub : S.ink}
              />
            )}
            <Metric label={data.dc.label.slice(0, 14)} value={`${data.dc.prob}%`} small />
            <Metric label={data.gol.label} value={`${data.gol.prob}%`} small />
          </div>
          <ReasonChips reasons={data.reasons} color={meta.color} glow={meta.glow}
            testid={`${dataTestId}-reasons`} />
        </>
      ) : slotKey === "gundam" ? (
        <GundamScores scores={data.scores} color={meta.color} glow={meta.glow}
          isFinished={isFinished} />
      ) : slotKey === "kage" ? (
        <>
          <div
            style={{
              fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700,
              fontSize: "1.15rem", color: S.ink, lineHeight: 1.2, marginBottom: "0.6rem",
              textShadow: `0 0 10px ${meta.glow}`,
            }}
          >
            Multigol Casa <span style={{ color: meta.color }}>{data.home?.band}</span>
            <span style={{ color: meta.color, margin: "0 0.4em" }}>+</span>
            Multigol Trasferta <span style={{ color: meta.color }}>{data.away?.band}</span>
          </div>
          <div style={{ display: "flex", gap: "1.5rem", flexWrap: "wrap" }}>
            <Metric label="Somma prob" value={`${data.sum_prob}%`} accent={meta.color} bold />
            <Metric label={`Casa ${data.home?.band}`} value={`${data.home?.prob}%`} small />
            <Metric label={`Trasferta ${data.away?.band}`} value={`${data.away?.prob}%`} small />
            {data.fair_odds != null && (
              <Metric label="Quota fair" value={data.fair_odds.toFixed(2)} accent={S.sub} />
            )}
          </div>
          <ReasonChips reasons={data.reasons} color={meta.color} glow={meta.glow}
            testid={`${dataTestId}-reasons`} />
        </>
      ) : (
        <>
          <div
            style={{
              fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700,
              fontSize: "1.3rem", color: S.ink, lineHeight: 1.1, marginBottom: "0.5rem",
              textShadow: `0 0 10px ${meta.glow}`,
            }}
          >
            {data.bet}
          </div>
          <div style={{ display: "flex", gap: "1.5rem", flexWrap: "wrap" }}>
            <Metric label="Probabilità" value={`${data.prob}%`} accent={meta.color} bold />
            {data.book_odds && <Metric label="Quota" value={data.book_odds.toFixed(2)} />}
            {slotKey === "shogun" && data.value_pct != null && (
              <Metric
                label="Edge"
                value={`+${data.value_pct.toFixed(1)}%`}
                accent={S.gold}
                bold
              />
            )}
          </div>
          <ReasonChips reasons={data.reasons} color={meta.color} glow={meta.glow}
            testid={`${dataTestId}-reasons`} />
        </>
      )}
    </div>
  );
}

function GundamScores({ scores, color, glow, isFinished }) {
  if (!scores || scores.length === 0) return null;
  return (
    <div
      data-testid="gundam-scores"
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${scores.length}, minmax(0,1fr))`,
        gap: "0.6rem",
      }}
    >
      {scores.map((s, i) => {
        const won = isFinished && s.outcome === "won";
        const lost = isFinished && s.outcome === "lost";
        const accent = won ? S.win : color;
        const accentGlow = won ? `rgba(34,255,163,0.55)` : glow;
        return (
          <div
            key={`${s.score}-${i}`}
            data-testid={`gundam-score-${i}`}
            style={{
              position: "relative",
              padding: "0.85rem 0.6rem 0.8rem",
              background: won ? "rgba(34,255,163,0.06)" : "rgba(255,255,255,0.02)",
              border: `1px solid ${won ? S.win + "60" : S.hairline2}`,
              textAlign: "center",
              boxShadow: won ? `0 0 18px ${accentGlow}, inset 0 0 12px rgba(34,255,163,0.15)` : "none",
              opacity: lost ? 0.55 : 1,
            }}
          >
            <div
              style={{
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: "0.55rem", letterSpacing: "0.2em", color: S.muted,
                textTransform: "uppercase", marginBottom: 4,
              }}
            >
              {i === 0 ? "più probabile" : i === 1 ? "seconda" : "terza"}
            </div>
            <div
              style={{
                fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 900,
                fontSize: "1.9rem", color: accent, lineHeight: 1,
                textShadow: `0 0 16px ${accentGlow}`,
              }}
            >
              {s.score}
            </div>
            <div
              style={{
                marginTop: "0.45rem",
                display: "flex", justifyContent: "center", gap: "0.75rem",
                fontFamily: "'JetBrains Mono',monospace", fontSize: "0.72rem",
              }}
            >
              <span style={{ color: S.sub }}>{s.prob}%</span>
              <span style={{ color: S.muted }}>·</span>
              <span style={{ color: S.ink, fontWeight: 600 }}>@ {s.fair_odds?.toFixed(2)}</span>
            </div>
            {s.reason && (
              <div
                style={{
                  marginTop: "0.45rem",
                  paddingTop: "0.45rem",
                  borderTop: `1px dashed ${S.hairline2}`,
                  fontFamily: "'JetBrains Mono',monospace",
                  fontSize: "0.55rem", letterSpacing: "0.08em",
                  color: accent, textTransform: "uppercase", lineHeight: 1.25,
                  textShadow: `0 0 6px ${accentGlow}`,
                }}
              >
                {s.reason}
              </div>
            )}
            {won && (
              <div
                style={{
                  position: "absolute", top: 6, right: 6,
                }}
              >
                <Check size={12} style={{ color: S.win, filter: `drop-shadow(0 0 6px ${S.win})` }} strokeWidth={3} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function Metric({ label, value, accent, bold, small }) {
  return (
    <div>
      <div
        style={{
          fontFamily: "'JetBrains Mono',monospace",
          fontSize: "0.58rem", letterSpacing: "0.15em", textTransform: "uppercase",
          color: S.muted,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: "'JetBrains Mono',monospace",
          fontSize: small ? "0.8rem" : bold ? "1.15rem" : "0.98rem",
          fontWeight: bold ? 700 : 500,
          color: accent || S.ink,
          textShadow: accent && bold ? `0 0 12px ${accent}80` : "none",
          marginTop: 2,
        }}
      >
        {value}
      </div>
    </div>
  );
}

function ReliabilityGauge({ rel }) {
  if (!rel) return null;
  const color = GRADE_COLOR[rel.grade] || S.muted;
  return (
    <div
      data-testid="samurai-reliability-gauge"
      style={{
        display: "flex", alignItems: "center", gap: "1.5rem",
        padding: "1.3rem 1.5rem",
        background: S.panel,
        border: `1px solid ${S.hairline2}`,
        borderTop: `2px solid ${color}`,
        boxShadow: `0 -8px 24px -10px ${color}80`,
      }}
    >
      <div
        style={{
          width: 76, height: 76, display: "flex", alignItems: "center", justifyContent: "center",
          background: `${color}15`, border: `2px solid ${color}`,
          fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 900,
          fontSize: "3rem", color,
          textShadow: `0 0 24px ${color}`, flexShrink: 0,
          boxShadow: `0 0 18px ${color}70, inset 0 0 16px ${color}40`,
        }}
      >
        {rel.grade}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700,
            fontSize: "0.72rem", letterSpacing: "0.3em", color: S.muted,
            textTransform: "uppercase", marginBottom: 4,
          }}
        >
          Affidabilità Pacchetto
        </div>
        <div
          style={{
            display: "flex", alignItems: "baseline", gap: "0.5rem",
            fontFamily: "'JetBrains Mono',monospace", marginBottom: "0.6rem",
          }}
        >
          <span style={{ fontSize: "1.9rem", fontWeight: 700, color, textShadow: `0 0 16px ${color}` }}>
            {rel.score}
          </span>
          <span style={{ fontSize: "0.75rem", color: S.muted }}>/ 100</span>
        </div>
        <div style={{ height: 5, background: S.hairline, position: "relative", overflow: "hidden" }}>
          <div
            style={{
              position: "absolute", inset: 0, width: `${rel.score}%`,
              background: `linear-gradient(to right, ${color}90, ${color})`,
              boxShadow: `0 0 12px ${color}`,
            }}
          />
        </div>
      </div>
    </div>
  );
}

function StrikeOverlay({ active }) {
  if (!active) return null;
  return (
    <div
      data-testid="samurai-strike-overlay"
      aria-hidden
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        pointerEvents: "none", overflow: "hidden",
      }}
    >
      {/* Red full-screen flash */}
      <div style={{
        position: "absolute", inset: 0,
        background: `radial-gradient(circle at 50% 50%, ${S.blood}55 0%, ${S.blood}20 35%, transparent 70%)`,
        animation: "strikeFlash 750ms ease-out forwards",
      }} />

      {/* Diagonal katana slash A (top-left → bottom-right) */}
      <div style={{
        position: "absolute", top: "50%", left: "50%",
        width: "140vw", height: "4px",
        background: `linear-gradient(to right, transparent 0%, ${S.blood} 45%, #fff 50%, ${S.blood} 55%, transparent 100%)`,
        boxShadow: `0 0 24px ${S.blood}, 0 0 48px ${S.bloodGlow}`,
        transform: "translate(-60vw, -60vh) rotate(30deg)",
        transformOrigin: "center",
        animation: "strikeSlashA 600ms cubic-bezier(0.2, 0.7, 0.1, 1) forwards",
      }} />

      {/* Diagonal katana slash B (top-right → bottom-left), slightly delayed */}
      <div style={{
        position: "absolute", top: "50%", left: "50%",
        width: "140vw", height: "4px",
        background: `linear-gradient(to right, transparent 0%, ${S.blood} 45%, #fff 50%, ${S.blood} 55%, transparent 100%)`,
        boxShadow: `0 0 24px ${S.blood}, 0 0 48px ${S.bloodGlow}`,
        transform: "translate(60vw, -60vh) rotate(-30deg)",
        transformOrigin: "center",
        animation: "strikeSlashB 600ms cubic-bezier(0.2, 0.7, 0.1, 1) 120ms forwards",
      }} />

      {/* Kanji 刀 that punches in at the centre */}
      <div style={{
        position: "absolute", top: "50%", left: "50%",
        transform: "translate(-50%, -50%)",
        fontFamily: "'Barlow Condensed', serif", fontWeight: 900,
        fontSize: "min(22vw, 280px)", lineHeight: 1,
        color: "var(--bc-ink, #fff)",
        textShadow: `0 0 16px ${S.blood}, 0 0 40px ${S.blood}, 0 0 80px ${S.bloodGlow}`,
        animation: "strikeKanji 700ms cubic-bezier(0.2, 0.7, 0.1, 1) 80ms forwards",
        opacity: 0,
      }}>刀</div>
    </div>
  );
}

function StrikeButton({ onClick, loading, label }) {
  const [hover, setHover] = useState(false);
  const glow = hover
    ? `0 0 0 1px ${S.blood}, 0 0 28px ${S.blood}, 0 0 60px ${S.bloodGlow}, inset 0 0 26px ${S.bloodGlow}`
    : `0 0 0 1px ${S.blood}, 0 0 16px ${S.bloodGlow}, 0 0 30px ${S.bloodGlow}, inset 0 0 18px ${S.bloodDim}`;
  const handleClick = () => { if (!loading) { playSfx("katana"); } onClick?.(); };
  return (
    <button
      data-testid="samurai-strike-btn"
      onClick={handleClick}
      disabled={loading}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: "relative",
        width: "100%",
        padding: "1.25rem 1.25rem",
        background: hover
          ? `linear-gradient(180deg, ${S.bloodDim} 0%, rgba(255,46,62,0.28) 100%)`
          : `linear-gradient(180deg, rgba(255,46,62,0.18) 0%, ${S.bloodDim} 100%)`,
        border: "none",
        color: S.ink,
        cursor: loading ? "wait" : "pointer",
        fontFamily: "'Barlow Condensed',sans-serif",
        fontWeight: 900,
        fontSize: "1.45rem",
        letterSpacing: "0.45em",
        textTransform: "uppercase",
        textShadow: `0 0 16px ${S.blood}, 0 0 32px ${S.bloodGlow}`,
        boxShadow: glow,
        transition: "all 0.2s ease",
        display: "flex", alignItems: "center", justifyContent: "center", gap: "0.9rem",
        overflow: "hidden",
      }}
    >
      <span aria-hidden style={{
        position: "absolute", left: 0, right: 0, top: 0, height: 1,
        background: `linear-gradient(to right, transparent, ${S.blood}, transparent)`,
        boxShadow: `0 0 10px ${S.bloodGlow}`,
      }} />
      <span aria-hidden style={{
        position: "absolute", left: 0, right: 0, bottom: 0, height: 1,
        background: `linear-gradient(to right, transparent, ${S.blood}, transparent)`,
        boxShadow: `0 0 10px ${S.bloodGlow}`,
      }} />
      {loading ? (
        <>
          <Loader2 size={22} style={{ animation: "spin 1s linear infinite" }} />
          <span>Colpo in corso…</span>
        </>
      ) : (
        <>
          <Sword size={22} style={{ filter: `drop-shadow(0 0 8px ${S.blood})` }} />
          <span>{label || "COLPISCI !"}</span>
          <Sword size={22} style={{ transform: "scaleX(-1)", filter: `drop-shadow(0 0 8px ${S.blood})` }} />
        </>
      )}
    </button>
  );
}

function TeamBlock({ team, align }) {
  const name = team?.name || "—";
  const logo = team?.logo;
  return (
    <div
      style={{
        display: "flex", flexDirection: "column", alignItems: "center", gap: "0.75rem",
        flex: 1, minWidth: 0, textAlign: "center",
      }}
    >
      <div
        style={{
          width: 76, height: 76, display: "flex", alignItems: "center", justifyContent: "center",
          background: S.panel, border: `1px solid ${S.hairline2}`,
          borderLeft: align === "left" ? `2px solid ${S.blood}` : `1px solid ${S.hairline2}`,
          borderRight: align === "right" ? `2px solid ${S.blood}` : `1px solid ${S.hairline2}`,
          boxShadow: align ? `0 0 20px -6px ${S.bloodGlow}` : "none",
        }}
      >
        {logo ? (
          <img
            src={logo}
            alt=""
            style={{ width: 50, height: 50, objectFit: "contain" }}
            onError={(e) => { e.target.style.display = "none"; }}
          />
        ) : (
          <Sword size={22} style={{ color: S.muted }} />
        )}
      </div>
      <div
        style={{
          fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 800,
          fontSize: "1.2rem", color: S.ink, lineHeight: 1.1,
          letterSpacing: "0.02em",
          overflow: "hidden", textOverflow: "ellipsis",
          display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
          maxWidth: "100%",
        }}
      >
        {name}
      </div>
    </div>
  );
}

function FinalScore({ score }) {
  if (!score) return null;
  return (
    <div
      data-testid="samurai-final-score"
      style={{
        fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 900,
        fontSize: "2.4rem", color: S.blood, lineHeight: 1,
        textShadow: `0 0 22px ${S.blood}, 0 0 44px ${S.bloodGlow}`,
        letterSpacing: "0.05em",
      }}
    >
      {score.home}<span style={{ margin: "0 0.2em", color: S.muted }}>–</span>{score.away}
    </div>
  );
}

export default function SamuraiMatchPage() {
  const { fixtureId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [header, setHeader] = useState(() => location.state?.match || null);
  const [headerLoading, setHeaderLoading] = useState(!location.state?.match);

  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [striking, setStriking] = useState(false);
  const [err, setErr] = useState(null);

  const [senseiEligible, setSenseiEligible] = useState(false);
  const [senseiOpen, setSenseiOpen] = useState(false);

  // Saved slots → "Mie Pronostici". Set<slotKey>.
  const [savedSlots, setSavedSlots] = useState(() => new Set());

  // Reset / rehydrate saved-slot badges whenever the user navigates to a
  // different fixture. Without this, switching from match A to match B
  // would carry A's "saved" highlights over (carrello appiccicato).
  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(SELECTIONS_KEY) || "[]");
      const existing = stored.find((s) => String(s.fixture_id) === String(fixtureId));
      const savedSources = new Set(
        (existing?.bets || [])
          .map((b) => b.source)
          .filter(Boolean)
      );
      const next = new Set();
      if (savedSources.has("samurai_shinobi")) next.add("shinobi");
      if (savedSources.has("samurai_samurai")) next.add("samurai");
      if (savedSources.has("samurai_shogun"))  next.add("shogun");
      if (savedSources.has("samurai_kage"))    next.add("kage");
      if (savedSources.has("samurai_gundam"))  next.add("gundam");
      setSavedSlots(next);
    } catch {
      setSavedSlots(new Set());
    }
  }, [fixtureId]);

  const addSlotToMyBets = async (slotKey, slotData) => {
    if (!header || savedSlots.has(slotKey)) return;
    const newBet = slotToBet(slotKey, slotData);
    if (!newBet) return;
    const { home_team, away_team, league } = header;
    const sessionId = getSessionId();
    try {
      // 1. localStorage merge — append new bet to any existing fixture entry.
      const stored = JSON.parse(localStorage.getItem(SELECTIONS_KEY) || "[]");
      const idx = stored.findIndex((s) => String(s.fixture_id) === String(fixtureId));
      let mergedBets = [];
      if (idx >= 0) {
        const existing = stored[idx];
        const dup = (existing.bets || []).some((b) => b.bet === newBet.bet);
        mergedBets = dup ? existing.bets : [...(existing.bets || []), newBet];
        stored[idx] = { ...existing, bets: mergedBets, added_at: new Date().toISOString() };
      } else {
        mergedBets = [newBet];
        stored.push({
          fixture_id: parseInt(fixtureId),
          match: `${home_team?.name} vs ${away_team?.name}`,
          home: home_team?.name, away: away_team?.name,
          home_logo: home_team?.logo, away_logo: away_team?.logo,
          league: league?.name, league_flag: league?.flag, country: league?.country,
          kick_off: header.date,
          bets: mergedBets,
          added_at: new Date().toISOString(),
        });
      }
      localStorage.setItem(SELECTIONS_KEY, JSON.stringify(stored));
      // 2. Backend persistence (cross-device). The endpoint replaces the
      // bets list for this fixture, so we send the merged set.
      await axios.post(`${API}/user-bets`, {
        session_id: sessionId,
        fixture_id: parseInt(fixtureId),
        home_team: home_team?.name, away_team: away_team?.name,
        league_name: league?.name, kick_off: header.date,
        bets: mergedBets,
      });
      setSavedSlots((prev) => {
        const next = new Set(prev);
        next.add(slotKey);
        return next;
      });
      playSfx("ding");
    } catch (e) {
      console.error("[Samurai] saveBet failed:", e);
    }
  };

  useEffect(() => {
    let cancelled = false;
    checkSenseiEligibility().then((eligible) => {
      if (!cancelled) setSenseiEligible(eligible);
    });
    return () => { cancelled = true; };
  }, []);

  // Fallback: if landed without state (deep link / refresh), scan recent days
  // to locate the fixture so header still renders.
  useEffect(() => {
    if (header) return;
    setHeaderLoading(true);
    let cancelled = false;
    const run = async () => {
      for (let offset = 0; offset <= 6; offset += 1) {
        const d = new Date();
        d.setDate(d.getDate() - offset);
        const iso = d.toISOString().slice(0, 10);
        try {
          const r = await axios.get(`${API}/fixtures/today?date=${iso}`);
          const all = (r.data?.leagues || []).flatMap((lg) =>
            (lg.matches || []).map((m) => ({ ...m, _league: lg })),
          );
          const match = all.find((m) => String(m.id) === String(fixtureId));
          if (match && !cancelled) {
            setHeader({
              id: match.id, date: match.date, status: match.status,
              goals: match.goals,
              home_team: match.home_team, away_team: match.away_team,
              league: match._league,
            });
            break;
          }
        } catch { /* keep trying */ }
      }
      if (!cancelled) setHeaderLoading(false);
    };
    run();
    return () => { cancelled = true; };
  }, [fixtureId, header]);

  const runStrike = (force = false) => {
    setErr(null);
    setStriking(true);
    // Fire the request immediately; keep the animation on top while it runs
    // so the user always sees at least ~750ms of strike FX even on fast APIs.
    const started = Date.now();
    setLoading(true);
    axios
      .get(`${API}/samurai/${fixtureId}`, { params: force ? { force: true } : {} })
      .then((r) => {
        const elapsed = Date.now() - started;
        const wait = Math.max(0, 750 - elapsed);
        setTimeout(() => {
          setPkg(r.data);
          setStriking(false);
          setLoading(false);
        }, wait);
      })
      .catch((e) => {
        setErr(e?.response?.data?.detail || "Impossibile generare il pacchetto.");
        setStriking(false);
        setLoading(false);
      });
  };

  const kickoff = formatKickoff(header?.date);
  const kickoffDate = formatDateFull(header?.date);
  const league = header?.league || pkg?.meta?.league;
  const homeTeam = header?.home_team || pkg?.meta?.home_team;
  const awayTeam = header?.away_team || pkg?.meta?.away_team;

  const isFinished = Boolean(
    pkg?.meta?.is_finished
    || (header?.status && ["FT", "AET", "PEN"].includes(header.status)),
  );
  const finalScore = pkg?.meta?.final_score
    || (isFinished && header?.goals
        ? { home: header.goals.home, away: header.goals.away }
        : null);

  return (
    <div data-testid="samurai-match-page" style={{ minHeight: "100vh", background: S.bg, color: S.ink }}>
      <SamuraiBackground />
      <StrikeOverlay active={striking} />
      <Navbar />

      <div style={{
        position: "relative", maxWidth: 820, margin: "0 auto",
        padding: "1.5rem 1.25rem 4rem", zIndex: 2,
        animation: striking ? "strikeShake 420ms ease-in-out 60ms" : "none",
      }}>
        <button
          data-testid="samurai-back"
          onClick={() => navigate("/samurai")}
          style={{
            display: "flex", alignItems: "center", gap: "0.5rem",
            background: "none", border: "none", color: S.muted,
            fontFamily: "'Barlow Condensed',sans-serif", fontSize: "0.75rem",
            letterSpacing: "0.2em", textTransform: "uppercase",
            cursor: "pointer", padding: 0, marginBottom: "1.25rem",
          }}
        >
          <ArrowLeft size={14} /> Seleziona altra partita
        </button>

        {/* MATCH HEADER */}
        <div
          data-testid="samurai-match-header"
          style={{
            background: S.panel,
            border: `1px solid ${S.hairline2}`,
            borderTop: `2px solid ${S.blood}`,
            boxShadow: `0 -8px 28px -10px ${S.bloodGlow}`,
            padding: "1.75rem 1.5rem 1.5rem",
            marginBottom: "1.5rem",
          }}
        >
          <div style={{ textAlign: "center", marginBottom: "1.25rem" }}>
            <div
              style={{
                display: "inline-flex", alignItems: "center", gap: "0.5rem",
                padding: "0.3rem 0.9rem",
                background: S.bloodDim, border: `1px solid ${S.blood}60`,
                boxShadow: `0 0 14px ${S.bloodGlow}`,
              }}
            >
              <Sword size={11} style={{ color: S.blood, filter: `drop-shadow(0 0 6px ${S.blood})` }} />
              <span style={{
                fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700,
                fontSize: "0.68rem", letterSpacing: "0.3em", color: S.blood,
                textShadow: `0 0 8px ${S.bloodGlow}`,
              }}>
                {isFinished ? "REVISIONE RETROATTIVA · 刀" : "SAMURAI PICKER · 刀"}
              </span>
            </div>
          </div>

          {headerLoading && !header ? (
            <div style={{ padding: "1.5rem", textAlign: "center" }}>
              <Loader2 size={18} style={{ color: S.blood, animation: "spin 1s linear infinite" }} />
            </div>
          ) : (
            <>
              <div style={{ display: "flex", alignItems: "flex-start", gap: "1rem" }}>
                <TeamBlock team={homeTeam} align="left" />
                <div
                  style={{
                    display: "flex", flexDirection: "column", alignItems: "center",
                    justifyContent: "center", padding: "0.5rem 0.25rem", minWidth: 86,
                  }}
                >
                  {isFinished && finalScore ? (
                    <>
                      <FinalScore score={finalScore} />
                      <div
                        style={{
                          marginTop: "0.5rem",
                          fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700,
                          fontSize: "0.6rem", letterSpacing: "0.3em", color: S.muted,
                          textTransform: "uppercase",
                        }}
                      >
                        Finale
                      </div>
                    </>
                  ) : (
                    <>
                      <span
                        style={{
                          fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 900,
                          fontSize: "1.9rem", color: S.blood, lineHeight: 1,
                          textShadow: `0 0 20px ${S.blood}, 0 0 40px ${S.bloodGlow}`,
                        }}
                      >
                        VS
                      </span>
                      <div
                        aria-hidden
                        style={{
                          marginTop: "0.6rem",
                          width: 30, height: 1,
                          background: `linear-gradient(to right, transparent, ${S.blood}, transparent)`,
                          boxShadow: `0 0 8px ${S.bloodGlow}`,
                        }}
                      />
                    </>
                  )}
                </div>
                <TeamBlock team={awayTeam} align="right" />
              </div>

              <div
                style={{
                  marginTop: "1.5rem",
                  paddingTop: "1rem",
                  borderTop: `1px solid ${S.hairline}`,
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  gap: "1rem", flexWrap: "wrap",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", minWidth: 0 }}>
                  {league?.logo && (
                    <img src={league.logo} alt=""
                      style={{ width: 20, height: 20, objectFit: "contain", flexShrink: 0 }}
                      onError={(e) => { e.target.style.display = "none"; }}
                    />
                  )}
                  <div style={{ minWidth: 0 }}>
                    <div style={{
                      fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700,
                      fontSize: "0.9rem", color: S.ink,
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }}>
                      {league?.name || "—"}
                    </div>
                    <div style={{
                      fontFamily: "'JetBrains Mono',monospace",
                      fontSize: "0.62rem", color: S.muted, letterSpacing: "0.15em",
                      textTransform: "uppercase",
                    }}>
                      {league?.country || ""}
                    </div>
                  </div>
                </div>
                <div
                  style={{
                    display: "flex", alignItems: "center", gap: "0.5rem",
                    padding: "0.45rem 0.85rem",
                    background: "var(--bc-panel-elev, var(--bc-panel-elev, rgba(255,255,255,0.03)))",
                    border: `1px solid ${S.hairline2}`,
                  }}
                >
                  <Clock size={12} style={{ color: S.blood }} />
                  <span style={{
                    fontFamily: "'JetBrains Mono',monospace",
                    fontSize: "0.78rem", fontWeight: 700, color: S.ink,
                    letterSpacing: "0.08em",
                  }}>
                    {kickoff}
                  </span>
                  {kickoffDate && (
                    <span style={{
                      fontFamily: "'JetBrains Mono',monospace",
                      fontSize: "0.64rem", color: S.muted, letterSpacing: "0.12em",
                      textTransform: "uppercase",
                    }}>
                      · {kickoffDate}
                    </span>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {/* PHASE 1: strike button */}
        {!pkg && !loading && (
          <div data-testid="samurai-prestrike">
            <p
              style={{
                fontFamily: "'DM Sans',sans-serif", fontSize: "0.95rem",
                color: S.sub, textAlign: "center", margin: "0 0 1.25rem",
                maxWidth: 540, marginLeft: "auto", marginRight: "auto",
              }}
            >
              {isFinished
                ? "Il Samurai rianalizzerà questa partita e mostrerà cosa avrebbe suggerito e cosa è effettivamente accaduto."
                : "Quando sei pronto, affila la katana e colpisci. Il Samurai analizzerà xG, Poisson, Monte Carlo, forma e quote per forgiare i tuoi quattro pronostici."}
            </p>
            <StrikeButton
              onClick={runStrike}
              loading={false}
              label={isFinished ? "RIVIVI IL COLPO !" : "COLPISCI !"}
            />
            {err && (
              <div
                style={{
                  marginTop: "1rem",
                  padding: "0.75rem 1rem", background: S.bloodDim,
                  border: `1px solid ${S.blood}60`, color: S.ink,
                  fontFamily: "'DM Sans',sans-serif", fontSize: "0.9rem",
                  boxShadow: `0 0 18px ${S.bloodGlow}`,
                }}
              >
                {err}
              </div>
            )}
          </div>
        )}

        {/* PHASE 2: loading */}
        {loading && (
          <div data-testid="samurai-striking">
            <StrikeButton onClick={() => {}} loading={true} />
            <p
              style={{
                fontFamily: "'Barlow Condensed',sans-serif",
                fontSize: "0.74rem", letterSpacing: "0.3em", color: S.muted,
                textTransform: "uppercase", textAlign: "center",
                marginTop: "1.25rem",
              }}
            >
              Il Samurai sta affilando la katana…
            </p>
          </div>
        )}

        {/* PHASE 3: results */}
        {pkg && !loading && (
          <>
            {pkg.blocked ? (
              <div
                data-testid="samurai-blocked"
                style={{
                  padding: "2rem 1.5rem", textAlign: "center",
                  background: S.panel, border: `1px solid ${S.hairline2}`,
                  borderTop: `2px solid ${S.blood}`,
                  boxShadow: `0 -6px 22px -8px ${S.bloodGlow}`,
                }}
              >
                <AlertTriangle size={22} style={{ color: S.blood, margin: "0 auto 0.75rem",
                  filter: `drop-shadow(0 0 8px ${S.blood})` }} />
                <div
                  style={{
                    fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 900,
                    fontSize: "1rem", letterSpacing: "0.25em", color: S.blood,
                    textTransform: "uppercase", marginBottom: "0.5rem",
                    textShadow: `0 0 10px ${S.bloodGlow}`,
                  }}
                >
                  Il Samurai preferisce non colpire
                </div>
                <p
                  style={{
                    fontFamily: "'DM Sans',sans-serif", fontSize: "0.9rem",
                    color: S.sub, maxWidth: 480, margin: "0 auto 1.25rem",
                  }}
                >
                  {pkg.block_reason}
                </p>

                <button
                  data-testid="samurai-force-btn"
                  onClick={() => runStrike(true)}
                  style={{
                    background: "transparent",
                    border: `1px solid ${S.blood}80`,
                    color: S.blood, cursor: "pointer",
                    padding: "0.75rem 1.4rem",
                    fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 900,
                    fontSize: "0.8rem", letterSpacing: "0.3em", textTransform: "uppercase",
                    textShadow: `0 0 10px ${S.bloodGlow}`,
                    boxShadow: `0 0 14px ${S.bloodGlow}, inset 0 0 10px ${S.bloodDim}`,
                    display: "inline-flex", alignItems: "center", gap: "0.6rem",
                    transition: "all 0.15s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = S.bloodDim;
                    e.currentTarget.style.color = S.ink;
                    e.currentTarget.style.boxShadow = `0 0 22px ${S.blood}, inset 0 0 14px ${S.bloodGlow}`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "transparent";
                    e.currentTarget.style.color = S.blood;
                    e.currentTarget.style.boxShadow = `0 0 14px ${S.bloodGlow}, inset 0 0 10px ${S.bloodDim}`;
                  }}
                >
                  <Sword size={13} style={{ filter: `drop-shadow(0 0 6px ${S.blood})` }} />
                  Forza il colpo
                  <Sword size={13} style={{ transform: "scaleX(-1)", filter: `drop-shadow(0 0 6px ${S.blood})` }} />
                </button>
                <div
                  style={{
                    marginTop: "0.85rem",
                    fontFamily: "'JetBrains Mono',monospace", fontSize: "0.62rem",
                    letterSpacing: "0.18em", textTransform: "uppercase",
                    color: S.muted,
                  }}
                >
                  Procedi a tuo rischio · affidabilità bassa
                </div>
              </div>
            ) : (
              <>
                {pkg.forced && (
                  <div
                    data-testid="samurai-forced-warning"
                    style={{
                      marginBottom: "1rem",
                      padding: "0.85rem 1rem",
                      background: S.bloodDim,
                      border: `1px solid ${S.blood}60`,
                      boxShadow: `0 0 14px ${S.bloodGlow}, inset 0 0 8px ${S.bloodDim}`,
                      display: "flex", gap: "0.7rem", alignItems: "flex-start",
                    }}
                  >
                    <AlertTriangle size={14} style={{
                      color: S.blood, marginTop: 2, flexShrink: 0,
                      filter: `drop-shadow(0 0 6px ${S.blood})`,
                    }} />
                    <div>
                      <div
                        style={{
                          fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 900,
                          fontSize: "0.72rem", letterSpacing: "0.28em", color: S.blood,
                          textTransform: "uppercase", marginBottom: 3,
                          textShadow: `0 0 8px ${S.bloodGlow}`,
                        }}
                      >
                        Colpo forzato · Poca affidabilità
                      </div>
                      <div
                        style={{
                          fontFamily: "'DM Sans',sans-serif", fontSize: "0.82rem",
                          color: S.sub, lineHeight: 1.4,
                        }}
                      >
                        {pkg.block_reason} I risultati sono forniti su richiesta: considera i pronostici con cautela.
                      </div>
                    </div>
                  </div>
                )}

                <BushidoCoherenceBanner data={pkg.bushido_coherence} />

                <ReliabilityGauge rel={pkg.reliability} />

                <AdvancedSignalsPanel adv={pkg.advanced} />

                {pkg.coherence_warnings?.length > 0 && (
                  <div
                    style={{
                      marginTop: "0.75rem", padding: "0.75rem 1rem",
                      background: "rgba(245,158,11,0.08)",
                      border: "1px solid rgba(245,158,11,0.35)",
                      display: "flex", gap: "0.6rem", alignItems: "flex-start",
                    }}
                  >
                    <Info size={12} style={{ color: "#F59E0B", marginTop: 3, flexShrink: 0 }} />
                    <div
                      style={{
                        fontFamily: "'JetBrains Mono',monospace", fontSize: "0.72rem",
                        color: S.sub,
                      }}
                    >
                      {pkg.coherence_warnings.join(" · ")}
                    </div>
                  </div>
                )}

                <KatanaDivider style={{ margin: "1.5rem 0" }} />

                <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                  <SlotCard slotKey="shinobi" data={pkg.shinobi} dataTestId="samurai-slot-shinobi" isFinished={isFinished} onAdd={() => addSlotToMyBets("shinobi", pkg.shinobi)} saved={savedSlots.has("shinobi")} />
                  <SlotCard slotKey="samurai" data={pkg.samurai} dataTestId="samurai-slot-samurai" isFinished={isFinished} onAdd={() => addSlotToMyBets("samurai", pkg.samurai)} saved={savedSlots.has("samurai")} />
                  <SlotCard slotKey="gundam"  data={pkg.gundam}  dataTestId="samurai-slot-gundam"  isFinished={isFinished} onAdd={() => addSlotToMyBets("gundam", pkg.gundam)}   saved={savedSlots.has("gundam")} />
                  <SlotCard slotKey="shogun"  data={pkg.shogun}  dataTestId="samurai-slot-shogun"  isFinished={isFinished} onAdd={() => addSlotToMyBets("shogun", pkg.shogun)}   saved={savedSlots.has("shogun")} />
                  <SlotCard slotKey="kage"    data={pkg.kage}    dataTestId="samurai-slot-kage"    isFinished={isFinished} onAdd={() => addSlotToMyBets("kage", pkg.kage)}       saved={savedSlots.has("kage")} />
                </div>

                {/* Sensei AI — owner-only deep analysis trigger */}
                {senseiEligible && (
                  <div style={{ marginTop: "1.25rem" }}>
                    {senseiOpen ? (
                      <SenseiPanel
                        fixtureId={fixtureId}
                        matchPayload={{
                          home_team: homeTeam?.name || "",
                          away_team: awayTeam?.name || "",
                          league_name: league?.name || "",
                          match_date: header?.date || "",
                          target_kitano: pkg?.samurai
                            ? { bet: `${pkg.samurai.dc?.label} + ${pkg.samurai.gol?.label}`, prob: pkg.samurai.combined_prob }
                            : (pkg?.shinobi
                                ? { bet: pkg.shinobi.bet, prob: pkg.shinobi.prob }
                                : {}),
                          samurai_package: {
                            shinobi: pkg?.shinobi,
                            samurai: pkg?.samurai,
                            gundam: pkg?.gundam,
                            shogun: pkg?.shogun,
                            kage: pkg?.kage,
                            reliability: pkg?.reliability,
                            advanced: pkg?.advanced,
                          },
                        }}
                        onClose={() => setSenseiOpen(false)}
                      />
                    ) : (
                      <button
                        data-testid="sensei-open-btn-samurai"
                        onClick={() => setSenseiOpen(true)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.6rem",
                          padding: "0.85rem 1rem",
                          width: "100%",
                          background: "color-mix(in srgb, var(--bc-accent) 5%, var(--bc-panel, #09090B))",
                          border: "1px dashed var(--bc-accent-medium)",
                          color: "var(--bc-accent)",
                          fontFamily: "'Barlow Condensed',sans-serif",
                          fontSize: "0.85rem",
                          letterSpacing: "0.18em",
                          textTransform: "uppercase",
                          fontWeight: 700,
                          cursor: "pointer",
                          transition: "all 0.18s ease",
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = "color-mix(in srgb, var(--bc-accent) 10%, var(--bc-panel, #09090B))"; e.currentTarget.style.borderStyle = "solid"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = "color-mix(in srgb, var(--bc-accent) 5%, var(--bc-panel, #09090B))"; e.currentTarget.style.borderStyle = "dashed"; }}
                      >
                        <Zap size={14} />
                        ⚔ ATTIVA SENSEI AI
                        <span style={{ marginLeft: "auto", fontSize: "0.62rem", fontFamily: "'JetBrains Mono',monospace", letterSpacing: "0.14em", color: "rgba(255,255,255,0.4)" }}>
                          OWNER · DEEP ANALYSIS
                        </span>
                      </button>
                    )}
                  </div>
                )}

                <button
                  data-testid="samurai-open-match"
                  onClick={() => navigate(`/match/${fixtureId}`)}
                  style={{
                    marginTop: "1.5rem", width: "100%", padding: "0.9rem",
                    background: "transparent", border: `1px solid ${S.hairline2}`,
                    color: S.muted, cursor: "pointer",
                    fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700,
                    fontSize: "0.75rem", letterSpacing: "0.22em",
                    textTransform: "uppercase",
                    transition: "all 0.15s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = S.blood;
                    e.currentTarget.style.color = S.blood;
                    e.currentTarget.style.boxShadow = `0 0 16px ${S.bloodGlow}`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = S.hairline2;
                    e.currentTarget.style.color = S.muted;
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  Apri analisi completa della partita →
                </button>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
